import React, { useEffect, useState } from "react";
import { api, fmtCurrency, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Download, RefreshCw } from "lucide-react";

const TABS = [
  { key: "sales", label: "Sales", needsDate: true },
  { key: "purchases", label: "Purchases", needsDate: true },
  { key: "profit", label: "Profit", needsDate: true },
  { key: "stock-valuation", label: "Stock Valuation", needsDate: false },
  { key: "dead-stock", label: "Dead Stock", needsDate: false, extra: "days" },
  { key: "fast-moving", label: "Fast Moving", needsDate: false, extra: "days" },
  { key: "reorder-suggestions", label: "Reorder Suggestions", needsDate: false },
];

export default function Reports() {
  const [tab, setTab] = useState("sales");
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const meta = TABS.find((t) => t.key === tab);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (meta.needsDate) { if (frm) params.frm = frm; if (to) params.to = to; }
      if (meta.extra === "days") params.days = days;
      const { data: res } = await api.get(`/reports/${tab}`, { params });
      setData(res);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const downloadXlsx = async () => {
    try {
      const params = new URLSearchParams({ fmt: "xlsx" });
      if (meta.needsDate) { if (frm) params.set("frm", frm); if (to) params.set("to", to); }
      if (meta.extra === "days") params.set("days", days);
      const token = localStorage.getItem("auth_token");
      const url = `${API}/reports/${tab}?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${tab}_${frm || "all"}_${to || "all"}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Excel downloaded");
    } catch (err) { toast.error("Download failed: " + err.message); }
  };

  const rows = data?.rows || [];
  const cols = rows[0] ? Object.keys(rows[0]) : [];

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Analytics</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Reports</h1>
          <p className="text-sm text-zinc-500 mt-2">Download Excel for any view; date range supported on sales/purchase/profit.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-px bg-zinc-200 border border-zinc-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`report-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-xs uppercase tracking-wider whitespace-nowrap ${
              tab === t.key ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap">
        {meta.needsDate && (
          <>
            <div><label className="label-eyebrow block mb-1">From</label><input data-testid="report-from-date" type="date" value={frm} onChange={(e) => setFrm(e.target.value)} className="border border-zinc-300 px-3 py-2 font-mono text-sm" /></div>
            <div><label className="label-eyebrow block mb-1">To</label><input data-testid="report-to-date" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-zinc-300 px-3 py-2 font-mono text-sm" /></div>
          </>
        )}
        {meta.extra === "days" && (
          <div><label className="label-eyebrow block mb-1">Days threshold</label><input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} className="border border-zinc-300 px-3 py-2 font-mono text-sm w-24" /></div>
        )}
        <button data-testid="report-apply" onClick={load} className="px-4 py-2 bg-zinc-950 text-white text-xs uppercase tracking-wider hover:bg-zinc-800 inline-flex items-center gap-2"><RefreshCw className="w-3 h-3" />Apply</button>
        <button data-testid="report-download-xlsx" onClick={downloadXlsx} disabled={!rows.length} className="px-4 py-2 bg-[#002FA7] text-white text-xs uppercase tracking-wider hover:bg-blue-900 disabled:opacity-50 inline-flex items-center gap-2"><Download className="w-3 h-3" />Download Excel</button>
      </div>

      {/* Summary */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
          {Object.entries(data.summary).map(([k, v]) => (
            <div key={k} className="bg-white p-4">
              <div className="label-eyebrow">{k.replace(/_/g, " ")}</div>
              <div className="mt-2 font-mono text-2xl font-bold text-zinc-950">{typeof v === "number" && (k.includes("total") || k.includes("value") || k.includes("revenue") || k.includes("cost") || k.includes("profit")) ? fmtCurrency(v) : v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="brutalist-card overflow-x-auto">
        {loading ? <div className="p-8 text-center text-sm text-zinc-500">Loading...</div> :
        rows.length === 0 ? <div className="p-12 text-center text-sm text-zinc-500"><BarChart3 className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No rows for these filters.</div> : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[10px] border-b border-zinc-200">
                {cols.map((c) => <th key={c} className="text-left px-3 py-3 font-semibold whitespace-nowrap">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 500).map((r, idx) => {
                // Composite key: prefer business identifiers when available, fall back to JSON snapshot
                const rowKey = r["Invoice #"] || r["Purchase Inv #"] || r["Part #"] ?
                  `${r["Invoice #"] || r["Purchase Inv #"] || ""}-${r["Part #"] || ""}-${r["Serial"] || idx}` :
                  `row-${idx}-${JSON.stringify(r).slice(0, 32)}`;
                return (
                <tr key={rowKey} className="border-t border-zinc-200 hover:bg-zinc-50">
                  {cols.map((c) => {
                    const v = r[c];
                    const isMoney = typeof v === "number" && (c.toLowerCase().includes("price") || c.toLowerCase().includes("total") || c.toLowerCase().includes("value") || c.toLowerCase().includes("revenue") || c.toLowerCase().includes("cost") || c.toLowerCase().includes("profit") || c.toLowerCase().includes("gst amount") || c === "MRP");
                    return <td key={c} className={`px-3 py-2 font-mono whitespace-nowrap ${isMoney ? "text-right" : ""}`}>{isMoney ? fmtCurrency(v) : (v ?? "—")}</td>;
                  })}
                </tr>
              );})}
            </tbody>
          </table>
        )}
        {rows.length > 500 && <div className="p-3 text-xs font-mono text-zinc-500 text-center">Showing first 500 rows of {rows.length}. Download Excel for full data.</div>}
      </div>
    </div>
  );
}
